import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

/** Max content widths (px) shared by every page. */
const PAGE_WIDTHS = {
  narrow: 760,
  default: 1180,
  wide: 1440,
};

/**
 * Page body: centred column with the shared gutters and vertical rhythm.
 * Gutters are 16px on phones so nothing scrolls horizontally at 390px.
 *
 * @param {object} props
 * @param {'narrow'|'default'|'wide'} [props.width='default'] Max content width.
 * @param {React.ReactNode} props.children Page content.
 * @param {object} [props.sx] Extra styles.
 * @returns {JSX.Element}
 */
export function PageContainer({ width = 'default', children, sx, ...rest }) {
  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: PAGE_WIDTHS[width] || PAGE_WIDTHS.default,
        mx: 'auto',
        boxSizing: 'border-box',
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 2, md: 2.5 },
        pb: { xs: 5, md: 7 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2, md: 2.5 },
        minWidth: 0,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * Consistent page header: optional breadcrumbs, title with actions on the
 * right (wrapping below on phones), and a short description / meta line.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.breadcrumbs] Breadcrumb trail rendered above the title.
 * @param {React.ReactNode} props.title Page title (rendered as the page's h1).
 * @param {React.ReactNode} [props.description] One-line description under the title.
 * @param {React.ReactNode} [props.meta] Secondary meta row (chips, dates).
 * @param {React.ReactNode} [props.actions] Buttons; the primary action should be last.
 * @param {React.ReactNode} [props.leading] Element before the title (icon, avatar).
 * @returns {JSX.Element}
 */
export function PageHeader({ breadcrumbs, title, description, meta, actions, leading }) {
  return (
    <Stack component="header" spacing={0.75} sx={{ minWidth: 0 }}>
      {breadcrumbs}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 1.25,
        }}
      >
        {leading && <Box sx={{ display: 'flex', flex: 'none' }}>{leading}</Box>}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            flex: '1 1 240px',
            minWidth: 0,
            fontSize: { xs: '1.375rem', sm: '1.75rem' },
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>
        {actions && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              maxWidth: '100%',
            }}
          >
            {actions}
          </Box>
        )}
      </Box>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760 }}>
          {description}
        </Typography>
      )}
      {meta && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          {meta}
        </Box>
      )}
    </Stack>
  );
}

/**
 * White bordered surface with an optional header row.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.title] Section title.
 * @param {React.ReactNode} [props.description] Short helper text under the title.
 * @param {React.ReactNode} [props.icon] Leading icon tile.
 * @param {React.ReactNode} [props.actions] Header actions (right side).
 * @param {boolean} [props.flush=false] Remove body padding (tables, lists).
 * @param {string} [props.component='section'] Root element.
 * @param {React.ReactNode} props.children Body.
 * @param {object} [props.sx] Extra styles.
 * @returns {JSX.Element}
 */
export function SectionCard({
  title,
  description,
  icon,
  actions,
  flush = false,
  component = 'section',
  children,
  sx,
  ...rest
}) {
  const hasHeader = Boolean(title || actions || description);
  return (
    <Box
      component={component}
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 'var(--q-radius-card, 16px)',
        overflow: 'hidden',
        minWidth: 0,
        ...sx,
      }}
      {...rest}
    >
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1.5,
            px: { xs: 2, sm: 2.5 },
            py: 1.75,
            borderBottom: children ? 1 : 0,
            borderColor: 'var(--q-border-subtle)',
          }}
        >
          {icon && <IconTile>{icon}</IconTile>}
          <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
            {title && (
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          {actions && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              {actions}
            </Box>
          )}
        </Box>
      )}
      {children && (
        <Box sx={flush ? undefined : { px: { xs: 2, sm: 2.5 }, py: 2 }}>{children}</Box>
      )}
    </Box>
  );
}

/**
 * Small rounded icon tile used in section headers and list rows.
 *
 * @param {object} props
 * @param {'accent'|'neutral'|'success'|'warning'|'danger'|'info'} [props.tone='accent'] Colour tone.
 * @param {number} [props.size=36] Tile size in px.
 * @param {React.ReactNode} props.children Icon.
 * @returns {JSX.Element}
 */
export function IconTile({ tone = 'accent', size = 36, children }) {
  const tones = {
    accent: ['var(--q-accent-soft)', 'var(--q-accent-text)'],
    neutral: ['var(--q-subtle)', 'var(--q-text-3)'],
    success: ['var(--q-success-soft)', 'var(--q-success-text)'],
    warning: ['var(--q-warning-soft)', 'var(--q-warning-text)'],
    danger: ['var(--q-danger-soft)', 'var(--q-danger-text)'],
    info: ['var(--q-info-soft)', 'var(--q-info-text)'],
  };
  const [bg, fg] = tones[tone] || tones.accent;
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: size,
        height: size,
        flex: 'none',
        borderRadius: 'var(--q-radius-lg, 10px)',
        bgcolor: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& .MuiSvgIcon-root': { fontSize: Math.round(size * 0.55) },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Toolbar row that sits at the top of a SectionCard (filters, actions).
 * Wraps on narrow screens.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Toolbar content.
 * @param {object} [props.sx] Extra styles.
 * @returns {JSX.Element}
 */
export function SurfaceToolbar({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        px: { xs: 1.5, sm: 2 },
        py: 1.5,
        borderBottom: 1,
        borderColor: 'var(--q-border-subtle)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
